import type {
  ModelVoteCount,
  OpenArenaResult,
  OpenArenaRun,
} from '@meridian-ssa/shared';
import { useEffect, useRef, useState } from 'react';

import { apiRequest } from '@/config';
import { logDebug, logWarn } from '@/lib/logger';
import { WeightedMatchEngine } from '@/lib/match-engine';

import type { MatchPair } from '../types';

const TARGET_MATCHES = 4;
const POLL_INTERVAL_MS = 2000;

// ============ Types ============

/** Result enriched with config name and status from API */
interface EnrichedResult extends OpenArenaResult {
  model_config_name: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

interface ResultStatus {
  finished: boolean; // No longer pending/running
  valid: boolean; // Completed with valid JSON
}

// ============ Progress Tracking Types ============

export interface MatchProgress {
  /** Number of unique models in the candidate matches that have returned valid results */
  readyModels: number;
  /** Total number of unique models needed for all candidate matches */
  totalModels: number;
}

// ============ Hook ============

/**
 * Manages arena run polling and match selection.
 *
 * Uses a vote-weighted selection strategy via WeightedMatchEngine:
 * 1. Fetch historical vote counts for all models
 * 2. Generate matches with probability proportional to 1/√(votesA × votesB)
 * 3. Models with fewer votes are prioritized to balance leaderboard data
 * 4. If a result fails/invalid, disqualify affected matches and re-select
 *
 * This ensures balanced data collection while showing matches progressively.
 */
export function useArenaPolling(runId: string | undefined) {
  const [run, setRun] = useState<OpenArenaRun | null>(null);
  const [readyMatches, setReadyMatches] = useState<MatchPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<MatchProgress>({
    readyModels: 0,
    totalModels: 0,
  });

  // Refs persist across polls without triggering re-renders
  const engineRef = useRef<WeightedMatchEngine | null>(null);
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!runId) return;

    let pollInterval: NodeJS.Timeout | null = null;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    // -------- Initialize weighted match engine (runs once) --------
    async function initializeEngine() {
      // Fetch results and vote counts in parallel
      const [resultsResponse, voteCountsResponse] = await Promise.all([
        apiRequest(`/api/arena/runs/${runId}/results`),
        apiRequest('/api/leaderboard/model-vote-counts').catch(() => {
          // Graceful fallback if vote counts unavailable
          logWarn('Failed to fetch vote counts, using uniform selection');
          return { counts: [] };
        }),
      ]);

      const results: EnrichedResult[] = resultsResponse.results || [];
      const voteCounts: ModelVoteCount[] = voteCountsResponse.counts || [];

      logDebug(
        `Initializing WeightedMatchEngine with ${results.length} results and ${voteCounts.length} vote counts`
      );

      // This should not happen if >= 2 model configs are enabled, since the backend
      // creates pending result records transactionally before returning from /arena/start.
      // Keep as defensive check in case of edge cases or API issues.
      if (results.length < 2) {
        logDebug(`Not enough results for matching: ${results.length} results`);
        return;
      }

      // Create weighted match engine
      engineRef.current = new WeightedMatchEngine({
        seed: runId!,
        globalVoteCounts: voteCounts,
        targetWindowSize: TARGET_MATCHES,
      });

      engineRef.current.initialize(results);
    }

    // -------- Update match readiness (runs on each poll) --------
    async function updateMatchReadiness() {
      const engine = engineRef.current;
      if (!engine) return;

      const response = await apiRequest(`/api/arena/runs/${runId}/results`);
      const results: EnrichedResult[] = response.results || [];

      // Build status lookup
      const statusMap = new Map<string, ResultStatus>(
        results.map(r => [
          r.id,
          {
            finished: r.status === 'completed' || r.status === 'failed',
            valid: r.status === 'completed' && r.is_valid_json === true,
          },
        ])
      );

      // Disqualify invalid results (engine handles idempotency via Set)
      for (const [resultId, status] of statusMap.entries()) {
        if (status.finished && !status.valid) {
          engine.disqualifyResult(resultId);
        }
      }

      // Get active window from engine
      const activeWindow = engine.getActiveWindow();

      // Calculate progress: unique models in active window and their readiness
      const modelsInWindow = new Set<string>();
      const readyModelsInWindow = new Set<string>();

      for (const match of activeWindow) {
        modelsInWindow.add(match.resultA_id);
        modelsInWindow.add(match.resultB_id);

        const statusA = statusMap.get(match.resultA_id);
        const statusB = statusMap.get(match.resultB_id);

        if (statusA?.valid) readyModelsInWindow.add(match.resultA_id);
        if (statusB?.valid) readyModelsInWindow.add(match.resultB_id);
      }

      // Update progress state
      setProgress({
        readyModels: readyModelsInWindow.size,
        totalModels: modelsInWindow.size,
      });

      // Add ready matches to display list
      setReadyMatches(currentMatches => {
        const displayedIds = new Set(
          currentMatches.map(m => `${m[0]}_${m[1]}`)
        );
        const newMatches = [...currentMatches];

        for (const match of activeWindow) {
          const matchId = `${match.resultA_id}_${match.resultB_id}`;
          if (displayedIds.has(matchId)) continue;

          const statusA = statusMap.get(match.resultA_id);
          const statusB = statusMap.get(match.resultB_id);

          if (statusA?.valid && statusB?.valid) {
            newMatches.push([match.resultA_id, match.resultB_id]);
            displayedIds.add(matchId);
          }
        }

        // Exit loading as soon as FIRST match is ready (greedy loading)
        if (newMatches.length >= 1) {
          setLoading(false);
        }

        // Return same reference if nothing added (prevents unnecessary re-renders)
        const added = newMatches.length > currentMatches.length;
        return added ? newMatches : currentMatches;
      });
    }

    // -------- Main poll loop --------
    async function poll() {
      try {
        const data = await apiRequest(`/api/arena/runs/${runId}`);
        setRun(data.run);

        if (!initializedRef.current) {
          await initializeEngine();
          initializedRef.current = true;
        }

        const status = data.run.status;

        if (status === 'pending' || status === 'running') {
          await updateMatchReadiness();
          if (!pollInterval) {
            pollInterval = setInterval(poll, POLL_INTERVAL_MS);
          }
        } else if (status === 'completed') {
          stopPolling();
          await updateMatchReadiness();
          // Run is complete - exit loading even if we don't have all target matches
          setLoading(false);
        } else {
          // Failed
          stopPolling();
          setError('Arena run failed');
          setLoading(false);
        }
      } catch (e) {
        stopPolling();
        setError(e instanceof Error ? e.message : 'An error occurred');
        setLoading(false);
      }
    }

    poll();
    return stopPolling;
  }, [runId]);

  // Calculate effective total: when run is done, use actual count; otherwise use target
  const isRunFinished = run?.status === 'completed' || run?.status === 'failed';
  const effectiveTotalMatches = isRunFinished
    ? readyMatches.length
    : TARGET_MATCHES;

  return {
    run,
    readyMatches,
    loading,
    error,
    setError,
    resultLoadingProgress: progress,
    effectiveTotalMatches,
  };
}
